import dom from '../utils/dom';
import IconSprites from '../utils/IconSprites';
import SvgIcon from '../utils/SvgIcon';
import type { FormControlElement } from './controls/FormControl';
import FormControl from './controls/FormControl';

function findOrCreateErrorHelpBlock(controlId: string) {
  const id = `tn-error-help-block-${controlId}`;

  const $el = document.getElementById(id);

  if ($el) {
    return $el as HTMLElement;
  }

  return dom.makeElement('div', { id, class: 'help-block' }) as HTMLElement;
}

function findHelpBlock(controlId: string): HTMLElement | null {
  const id = `tn-help-block-${controlId}`;
  return document.getElementById(id);
}

function findOrCreateRequiredIndicator($el: Element) {
  let $indicator = dom.findFrom($el, '.tn-required-field') as HTMLElement;

  if (!$indicator) {
    $indicator = dom.makeElement('span', { 'aria-hidden': true, class: 'tn-required-field' });
    $indicator.innerText = 'Required';
  }

  return $indicator;
}

function findErrorIconContainers($group: Element) {
  const preferredIconContainers = dom.findAllFrom($group, '.tn-form-label-contents');

  if (preferredIconContainers.length) {
    return preferredIconContainers;
  }

  return dom.findAllFrom($group, '.control-label');
}

class ControlGroup {
  $group: HTMLElement;

  id: string;

  $requiredIndicator: HTMLElement;

  $errorHelpBlock: HTMLElement;

  $helpBlock: HTMLElement | null;

  input: FormControl<FormControlElement>;

  order: number;

  constructor(id: string, $group: HTMLElement, input: FormControl<FormControlElement>, order = -1) {
    this.id = id;
    this.$group = $group;
    this.input = input;
    this.order = order;

    this.startListening();
    this.$errorHelpBlock = findOrCreateErrorHelpBlock(this.id);
    this.$requiredIndicator = findOrCreateRequiredIndicator($group);
    this.$helpBlock = findHelpBlock(this.id);

    if (this.$helpBlock) {
      this.input.appendToAttribute('aria-describedby', this.$helpBlock.id);
    }

    if (this.$errorHelpBlock.isConnected) {
      this.input.appendToAttribute('aria-describedby', this.$errorHelpBlock.id);
    }
  }

  clearErrors() {
    this.$group.classList.remove('has-error');

    if (this.$errorHelpBlock.parentElement) {
      dom.remove(this.$errorHelpBlock);
    }

    dom.findAllFrom(this.$group, '.control-label--with-icon')
      .forEach((label) => {
        label.classList.remove('control-label--with-icon');
      });

    dom.findAllFrom(this.$group, '.control-label__icon-error')
      .forEach(($icon) => dom.remove($icon));

    this.input.isInvalid = false;
    this.input.dropFromAttribute('aria-describedby', this.$errorHelpBlock.id);
  }

  showError(message: string) {
    this.$errorHelpBlock.innerHTML = message;
    this.$group.classList.add('has-error');
    this.$group.appendChild(this.$errorHelpBlock);

    findErrorIconContainers(this.$group)
      .filter(($el) => !$el.classList.contains('control-label--with-icon'))
      .forEach(($el) => {
        const icon = SvgIcon.getElement(IconSprites.XCircleFill, 'control-label__icon-error');

        $el.classList.add('control-label--with-icon');
        $el.prepend(icon);
      });

    this.input.appendToAttribute('aria-describedby', this.$errorHelpBlock.id);
    this.input.isInvalid = true;
  }

  isVisible() {
    return dom.isVisible(this.$group);
  }

  setIsRequired(value: boolean) {
    this.input.isRequired = value;

    const $indicator = this.$requiredIndicator;

    if (value) {
      if (!$indicator.parentElement) {
        dom.findFrom(this.$group, '.control-label')?.append($indicator);
      }
    } else {
      if ($indicator.parentElement) {
        dom.remove(this.$requiredIndicator);
      }
    }
  }

  show() {
    dom.unhide(this.$group);
  }

  hide() {
    dom.hide(this.$group);
  }

  disable() {
    this.input.isDisabled = true;
  }

  enable() {
    this.input.isDisabled = false;
  }

  startListening() {
    this.input.startListening();
  }

  stopListening() {
    this.input.stopListening();
  }
}

export default ControlGroup;
